"use client"

import type {
  Feedback,
  FeedbackAttachment,
  FeedbackCategory,
  FeedbackMessage,
  FeedbackStatus,
} from "@stockline/shared/types/core/feedback"
import { useCallback } from "react"
import { FEEDBACK_TABLE } from "../../../lib/offline/resources/core/feedback/db"
import { FEEDBACK_MESSAGE_TABLE } from "../../../lib/offline/resources/core/feedback-message/db"
import { useAISync } from "../../ai/providers/AISyncProvider"

export interface CreateFeedbackInput {
  title: string
  description?: string
  category?: FeedbackCategory
  pageUrl?: string | null
  attachments?: FeedbackAttachment[]
}

export interface CreateFeedbackMessageInput {
  feedbackId: string
  body: string
  attachments?: FeedbackAttachment[]
}

export function useFeedbackMutations() {
  const { storage, changeQueue, engine } = useAISync()

  const createFeedback = useCallback(
    async (input: CreateFeedbackInput): Promise<Feedback> => {
      const id = crypto.randomUUID()
      const now = new Date().toISOString()
      const row: Feedback = {
        id,
        companyId: "",
        authorUserId: "",
        title: input.title,
        description: input.description ?? "",
        category: input.category ?? "ask",
        status: "open",
        pageUrl: input.pageUrl ?? null,
        attachments: input.attachments ?? [],
        syncId: 0,
        createdAt: now,
        updatedAt: now,
        deletedAt: null,
      }
      await storage.put(FEEDBACK_TABLE, row)
      await changeQueue.enqueue("feedbacks", { op: "create", id, data: row })
      void engine.pushChanges("feedbacks")
      return row
    },
    [storage, changeQueue, engine]
  )

  const updateFeedbackStatus = useCallback(
    async (id: string, status: FeedbackStatus): Promise<void> => {
      const existing = await storage.get<Feedback>(FEEDBACK_TABLE, id)
      // Guard: missing local row means the click raced a delete or a not-yet-
      // synced server row. Skipping silently is the correct UX (next sync
      // tick will surface the real state).
      if (!existing) return
      const now = new Date().toISOString()
      await storage.put(FEEDBACK_TABLE, {
        ...existing,
        status,
        updatedAt: now,
      })
      await changeQueue.enqueue("feedbacks", {
        op: "update",
        id,
        data: { status },
      })
      void engine.pushChanges("feedbacks")
    },
    [storage, changeQueue, engine]
  )

  const deleteFeedback = useCallback(
    async (id: string): Promise<void> => {
      await storage.delete(FEEDBACK_TABLE, id)
      await changeQueue.enqueue("feedbacks", { op: "delete", id })
      void engine.pushChanges("feedbacks")
    },
    [storage, changeQueue, engine]
  )

  const sendMessage = useCallback(
    async (input: CreateFeedbackMessageInput): Promise<FeedbackMessage> => {
      const id = crypto.randomUUID()
      const now = new Date().toISOString()
      const row: FeedbackMessage = {
        id,
        companyId: "",
        feedbackId: input.feedbackId,
        authorUserId: "",
        body: input.body,
        // `isStaff` is server-authoritative — derived from the user's email
        // domain at push time. The optimistic local copy gets `false`; the
        // SSE round-trip flips it for staff users.
        isStaff: false,
        attachments: input.attachments ?? [],
        syncId: 0,
        createdAt: now,
        updatedAt: now,
        deletedAt: null,
      }
      await storage.put(FEEDBACK_MESSAGE_TABLE, row)
      await changeQueue.enqueue("feedback-messages", {
        op: "create",
        id,
        data: row,
      })
      void engine.pushChanges("feedback-messages")
      return row
    },
    [storage, changeQueue, engine]
  )

  return { createFeedback, updateFeedbackStatus, deleteFeedback, sendMessage }
}
